/**
 * Browser implementation of `Platform`.
 *
 * Notes and Sync State live in IndexedDB, settings in localStorage. Deletion
 * writes a tombstone instead of dropping the record, exactly like the desktop
 * store, so both backends hand a future sync engine the same history.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import { byRecency, makeNote, toSummary } from '@shared/notes'
import {
  applySyncStatePatch,
  assertSyncStatePatch,
  normaliseSyncState,
} from '@shared/sync-state'
import {
  DEFAULT_SETTINGS,
  type Note,
  type NoteId,
  type NotesStore,
  type Platform,
  type Settings,
  type SettingsStore,
  type SyncState,
  type SyncStateStore,
  type ThemePreference,
} from '@shared/types'

const DB_NAME = 'jot'
const DB_VERSION = 1
const STORE = 'notes'
const SETTINGS_KEY = 'jot:settings'

/**
 * Sync State, in an IndexedDB database of its own.
 *
 * IndexedDB because of what the record holds: the Last-Agreed Body on every accepted
 * push and a Pending Conflict's body while one is waiting, so two Scratchpads at
 * once, bounded only by `MAX_SCRATCHPAD_BYTES` apiece. Every shipping browser's
 * localStorage quota is single-digit MiB of *UTF-16*, so there is a supported
 * Scratchpad size at which a single key provably cannot hold the record — and
 * `writeSyncState` throws rather than degrading, deliberately, so that size is where
 * an accepted push stops being recordable. It was in localStorage because it started
 * out as a handful of scalars; a value the size of a note belongs where the notes are.
 *
 * A database of its own, rather than a second store inside `jot`, and that is not
 * tidiness. Adding a store means `DB_VERSION` 2, and a version change is the one
 * migration in this codebase that is genuinely dangerous — twice over. It runs
 * `upgrade` against every existing database, where a `createObjectStore('notes')` not
 * guarded on `oldVersion` throws, `openDB` rejects and `createNoteBackend` falls back
 * to memory: every existing note apparently gone. And it fires `blocked` against any
 * tab still holding version 1, where `openDB` never settles on its own — no timeout,
 * no rejection — so `createWebPlatform` never resolves and the panel never paints.
 * The web build supports several tabs deliberately (`NOTES_TICK_KEY`), so both are
 * reachable. A first open of a database that does not exist yet has neither hazard:
 * there is no store to collide with and no older connection to be blocked by, and a
 * database that stays at version 1 never upgrades again.
 *
 * It also keeps Sync State out of the notes themselves, which is the rule the desktop
 * store states at length and CONTEXT.md makes part of the term: this record belongs to
 * one device, so it must not travel with a copy of the notes.
 */
const SYNC_DB_NAME = 'jot-sync'
const SYNC_DB_VERSION = 1
const SYNC_STORE = 'sync-state'
/** The one record in it. Sync State belongs to this device, so there is only ever one. */
const SYNC_STATE_KEY = 'state'
/**
 * Where Sync State was written before it moved, and still read from until the first
 * write lands (`readSyncState`).
 *
 * Picked up rather than abandoned because of the one field in it that cannot be
 * rediscovered: dropping the `scratchpadId` would unpair the device and drop it back
 * to recency, which is the divergence ADR-0001 exists to prevent.
 */
const LEGACY_SYNC_STATE_KEY = 'jot:sync-state'
/**
 * Bumped on every note write. IndexedDB has no cross-tab change event, but
 * `storage` fires in every *other* tab, which is exactly the audience that
 * needs to hear about it — the same trick `onSettingsChanged` already uses.
 */
const NOTES_TICK_KEY = 'jot:notes-tick'

/**
 * The web build cannot ask Electron for `app.getVersion()`. The build injects
 * the real number as an env var; the fallback tracks package.json.
 */
const APP_VERSION: string = import.meta.env.VITE_APP_VERSION ?? '0.1.0'

interface JotDB extends DBSchema {
  notes: {
    key: NoteId
    value: Note
    indexes: { updatedAt: number }
  }
}

interface JotSyncDB extends DBSchema {
  'sync-state': {
    key: string
    value: SyncState
  }
}

/* -------------------------------------------------------------------------- */
/* Notes                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * The three primitives the note store needs. Isolating them lets IndexedDB be
 * swapped for the in-memory fallback without duplicating any store logic.
 */
interface NoteBackend {
  all(): Promise<Note[]>
  get(id: NoteId): Promise<Note | undefined>
  put(note: Note): Promise<void>
}

function idbBackend(db: IDBPDatabase<JotDB>): NoteBackend {
  return {
    // Reading through the `updatedAt` index means the rows arrive already
    // grouped by recency, which is the only order the switcher ever wants.
    all: () => db.getAllFromIndex(STORE, 'updatedAt'),
    get: (id) => db.get(STORE, id),
    put: async (note) => {
      await db.put(STORE, note)
    },
  }
}

/**
 * Last resort for browsers that refuse persistent storage (private windows,
 * storage blocked by policy). Notes do not survive a reload, but the app runs
 * instead of showing a dead editor.
 */
function memoryBackend(): NoteBackend {
  const records = new Map<NoteId, Note>()
  return {
    all: async () => [...records.values()],
    get: async (id) => records.get(id),
    put: async (note) => {
      records.set(note.id, note)
    },
  }
}

async function createNoteBackend(): Promise<NoteBackend> {
  try {
    const db = await openDB<JotDB>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        const store = database.createObjectStore(STORE, { keyPath: 'id' })
        store.createIndex('updatedAt', 'updatedAt')
      },
    })
    // Some private-browsing modes open the database happily and then reject
    // every transaction, so prove it works before committing to it.
    await db.count(STORE)
    return idbBackend(db)
  } catch {
    return memoryBackend()
  }
}

/** Tombstoned notes stay on disk for sync but must never reach the UI. */
function isLive(note: Note): boolean {
  return note.deletedAt === null
}

/** Tell the other tabs something changed. Storage may be blocked; that is fine. */
function announceNotesChanged(): void {
  try {
    window.localStorage.setItem(NOTES_TICK_KEY, String(Date.now()))
  } catch {
    // Private windows and blocked storage. The tab that wrote is still correct.
  }
}

function createNotesStore(backend: NoteBackend): NotesStore {
  return {
    async list() {
      const notes = await backend.all()
      return notes.filter(isLive).map(toSummary).sort(byRecency)
    },

    // Null means absent, which here includes a tombstone: the record is kept for
    // sync and must never reach the UI. A transaction that failed rejects instead,
    // and the difference is load-bearing in the same way it is on the desktop
    // (issue #12) — the caller recreates what is absent and must not write over
    // what it merely could not read.
    async get(id) {
      const note = await backend.get(id)
      return note && isLive(note) ? note : null
    },

    async create(body) {
      const note = makeNote(body)
      await backend.put(note)
      announceNotesChanged()
      return note
    },

    async update(id, body) {
      const existing = await backend.get(id)
      // An upsert, not a lookup failure: autosave must never throw away
      // keystrokes because the record vanished (deleted in another tab, store
      // wiped mid-session). Clearing `deletedAt` resurrects a tombstone for the
      // same reason — the user is actively typing into it.
      const note: Note = existing
        ? {
            ...existing,
            body,
            updatedAt: Date.now(),
            deletedAt: null,
            rev: existing.rev + 1,
          }
        : { ...makeNote(body), id }

      await backend.put(note)
      announceNotesChanged()
      return note
    },

    async remove(id) {
      const existing = await backend.get(id)
      if (!existing || !isLive(existing)) return
      await backend.put({ ...existing, deletedAt: Date.now(), rev: existing.rev + 1 })
      announceNotesChanged()
    },
  }
}

/* -------------------------------------------------------------------------- */
/* Settings                                                                   */
/* -------------------------------------------------------------------------- */

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function asBool(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback
}

function asString(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback
}

/** An empty accelerator would silently unregister the global shortcut. */
function asShortcut(value: unknown, fallback: string): string {
  const trimmed = typeof value === 'string' ? value.trim() : ''
  return trimmed === '' ? fallback : trimmed
}

function asTheme(value: unknown, fallback: ThemePreference): ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark' ? value : fallback
}

/** 12..20 is the range the Settings dialog offers; anything else is a bug or a hand edit. */
function asFontSize(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return Math.min(20, Math.max(12, Math.round(value)))
}

/**
 * Merge an untrusted partial over a known-good base, validating field by field.
 * The same function parses whatever is sitting in localStorage and applies a
 * `set()` patch, which is what keeps the two paths from validating differently.
 */
function normalise(raw: unknown, base: Settings): Settings {
  const src: Record<string, unknown> = isRecord(raw) ? raw : {}
  const sync: Record<string, unknown> = isRecord(src.sync) ? src.sync : {}

  return {
    shortcut: asShortcut(src.shortcut, base.shortcut),
    theme: asTheme(src.theme, base.theme),
    fontSize: asFontSize(src.fontSize, base.fontSize),
    hideOnBlur: asBool(src.hideOnBlur, base.hideOnBlur),
    launchAtLogin: asBool(src.launchAtLogin, base.launchAtLogin),
    showInDock: asBool(src.showInDock, base.showInDock),
    lineWrap: asBool(src.lineWrap, base.lineWrap),
    spellCheck: asBool(src.spellCheck, base.spellCheck),
    sync: {
      enabled: asBool(sync.enabled, base.sync.enabled),
      // An empty endpoint is a legitimate value, so this one is a type check only.
      endpoint: asString(sync.endpoint, base.sync.endpoint),
      // The token is the first secret this store has held, and it lands in
      // localStorage beside everything else — which is the whole of what a
      // browser gives us to keep it in. Type-checked and never logged; whether
      // the endpoint it is about to travel over is one Jot will talk to is
      // decided at the request, by `scratchpadEndpoint`.
      token: asString(sync.token, base.sync.token),
      deviceLabel: asString(sync.deviceLabel, base.sync.deviceLabel),
    },
  }
}

/**
 * Holds settings when localStorage itself is unusable. Reads prefer it over the
 * (broken) storage so a session stays coherent even though nothing persists.
 */
let memorySettings: Settings | null = null

function readSettings(): Settings {
  if (memorySettings) return memorySettings
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY)
    if (raw === null) return DEFAULT_SETTINGS
    return normalise(JSON.parse(raw), DEFAULT_SETTINGS)
  } catch {
    // Blocked storage or corrupt JSON. Defaults beat an unusable app.
    return DEFAULT_SETTINGS
  }
}

function writeSettings(next: Settings): void {
  try {
    window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next))
    memorySettings = null
  } catch {
    memorySettings = next
  }
}

function createSettingsStore(): SettingsStore {
  return {
    get: async () => readSettings(),
    set: async (patch) => {
      const next = normalise(patch, readSettings())
      writeSettings(next)
      return next
    },
  }
}

/* -------------------------------------------------------------------------- */
/* Sync State                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * The Sync State database, or null when this browser refuses one — a private
 * window, storage blocked by policy.
 *
 * Null rather than a memory fallback, unlike the note backend next door: a Sync
 * State that survives only until the tab closes is worse than none at all, which is
 * the whole of what `writeSyncState` is about. What the notes do about the same
 * refusal is a separate decision, and `createNoteBackend` makes it — a dead editor
 * is worse than notes that do not persist, and Jot has no sync to lose there.
 */
async function openSyncDB(): Promise<IDBPDatabase<JotSyncDB> | null> {
  try {
    const db = await openDB<JotSyncDB>(SYNC_DB_NAME, SYNC_DB_VERSION, {
      upgrade(database) {
        database.createObjectStore(SYNC_STORE)
      },
    })
    // The same proof the note backend takes, for the same reason: some
    // private-browsing modes open a database happily and then reject every
    // transaction against it.
    await db.count(SYNC_STORE)
    return db
  } catch {
    return null
  }
}

/**
 * What the localStorage key still holds, for a device paired before Sync State moved.
 *
 * Read at most until the first write, which puts the whole merged record in
 * IndexedDB and clears the key. Whatever is there is projected by
 * `normaliseSyncState` like any other stored record, so a value from a build whose
 * shape has since changed costs a round trip and never the pairing.
 */
function readLegacySyncState(): unknown {
  try {
    return JSON.parse(window.localStorage.getItem(LEGACY_SYNC_STATE_KEY) ?? 'null')
  } catch {
    // Blocked storage, or a value that is not JSON at all. `normaliseSyncState`
    // turns both into the unpaired value, so there is nothing to branch on.
    return null
  }
}

/**
 * Sync State as this device recorded it, wherever that is.
 *
 * IndexedDB first and localStorage only when it holds nothing, so the two can never
 * disagree about which is current: the legacy key is never written again, and it is
 * removed once a record exists beside it.
 *
 * Absent, unreadable and nonsensical all answer with the unpaired value, exactly as
 * the desktop store does — a device that cannot read its Sync State is
 * indistinguishable from one that never had any, and ADR-0001 keeps recency as the
 * bootstrap path for it. Reporting the failure is the *write* path's job, where the
 * engine acts on it.
 */
async function readSyncState(db: IDBPDatabase<JotSyncDB> | null): Promise<SyncState> {
  if (db !== null) {
    try {
      const stored = await db.get(SYNC_STORE, SYNC_STATE_KEY)
      if (stored !== undefined) return normaliseSyncState(stored)
    } catch {
      // A transaction that failed. The legacy key may still hold a pairing, and if
      // it does not, this is the unpaired value either way.
    }
  }
  return normaliseSyncState(readLegacySyncState())
}

/**
 * Merge a patch into the record and write it, or throw — deliberately unlike
 * `writeSettings` next door, which keeps a memory copy when storage refuses.
 *
 * Settings are preferences: holding them in memory for the session is better than
 * losing them, and the token has to work in the tab the user pasted it into. Sync
 * State is a claim about what this device and a Jot Server agreed on, and a claim
 * that survives only until the tab closes is worse than no claim at all — the
 * engine would report an agreement, stop pushing, and then re-pair on the next load
 * and meet a Conflict on a device where only one side ever edited. It says as much:
 * "a write that does not land is reported rather than ignored". Refused storage on
 * this platform means a private window or a policy, and neither clears on its own,
 * so the honest answer is that sync is unavailable here.
 *
 * One `readwrite` transaction over the whole merge, because the base is read from the
 * same record the patch is written back to: two tabs interleaving a read and a write
 * would otherwise have the second silently drop what the first recorded, and one of
 * the two values at stake is a Pending Conflict.
 */
async function writeSyncState(
  db: IDBPDatabase<JotSyncDB> | null,
  patch: Partial<SyncState>,
): Promise<SyncState> {
  if (db === null) throw new Error('Could not write the sync state')

  let next: SyncState
  try {
    const tx = db.transaction(SYNC_STORE, 'readwrite')
    const stored = await tx.store.get(SYNC_STATE_KEY)
    // Nothing but IndexedDB requests is awaited between the read and the write: a
    // transaction commits as soon as control returns to the event loop with none
    // pending, and the merge would then be neither atomic nor complete.
    next = normaliseSyncState(
      applySyncStatePatch(normaliseSyncState(stored ?? readLegacySyncState()), patch),
    )
    await tx.store.put(next, SYNC_STATE_KEY)
    await tx.done
  } catch {
    throw new Error('Could not write the sync state')
  }

  // Only now, and only best-effort: the record is in IndexedDB, which `readSyncState`
  // prefers, so a key that outlives this call is read by nothing.
  try {
    window.localStorage.removeItem(LEGACY_SYNC_STATE_KEY)
  } catch {
    // Blocked storage. Nothing reads the key any more either way.
  }

  return next
}

function createSyncStateStore(db: IDBPDatabase<JotSyncDB> | null): SyncStateStore {
  return {
    get: () => readSyncState(db),
    // Checked before storage is touched, by the same validator the desktop store
    // applies over IPC. Normalising an unusable id to "unpaired" here instead would
    // report success while unpairing the device, and would mean `set` promised
    // something different on each platform.
    //
    // `async` for the same reason, and it is load-bearing rather than style: a
    // refused patch has to *reject*, because that is all the desktop store can do —
    // its throw happens inside an `ipcMain.handle` — and a caller that awaits both
    // stores must not have to catch one of them synchronously.
    set: async (patch) => writeSyncState(db, assertSyncStatePatch(patch)),
  }
}

/* -------------------------------------------------------------------------- */

export async function createWebPlatform(): Promise<Platform> {
  // Together: two databases, neither on the other's path, and the panel cannot paint
  // until this function answers.
  const [backend, syncDb] = await Promise.all([createNoteBackend(), openSyncDB()])

  const platform: Platform = {
    info: {
      kind: 'web',
      appVersion: APP_VERSION,
      electronVersion: null,
      chromeVersion: null,
      nodeVersion: null,
      os: 'web',
      storageLabel: 'This browser (IndexedDB)',
      notesPath: null,
      // There is no hostname to ask for here, and a user agent string would be
      // noise in a Resolution summary. `pushedBy` is a hint either way, and the
      // Settings field is how a second browser gets a name of its own.
      deviceLabel: 'browser',
    },

    notes: createNotesStore(backend),
    settings: createSettingsStore(),
    syncState: createSyncStateStore(syncDb),

    window: {
      // No panel to hide, no process to quit, no folder to reveal.
      hide: () => {},
      quit: () => {},
      openNotesFolder: () => {},
    },

    onSettingsChanged(listener) {
      // `storage` fires only in the *other* tabs, which is precisely the change
      // this renderer cannot see for itself. A null key means the whole store
      // was cleared, so re-read rather than trusting `event.newValue`.
      const onStorage = (event: StorageEvent): void => {
        if (event.key !== null && event.key !== SETTINGS_KEY) return
        listener(readSettings())
      }
      window.addEventListener('storage', onStorage)
      return () => window.removeEventListener('storage', onStorage)
    },

    onNotesChanged(listener) {
      // The database is shared by every tab on this origin, so "nothing outside
      // this renderer writes" is false for exactly the backend that can be open
      // more than once. Without this the switcher in one tab never learns about
      // notes created, renamed or deleted in another.
      const onStorage = (event: StorageEvent): void => {
        if (event.key !== null && event.key !== NOTES_TICK_KEY) return
        listener()
      }
      window.addEventListener('storage', onStorage)
      return () => window.removeEventListener('storage', onStorage)
    },
  }

  return platform
}
